import { useState, useCallback, useRef, useEffect } from "react";
import Axios from "axios";
import { environment } from "../../environment";
import { errorCatcher } from "../../hooks";
import { useLogin } from "../login";
import { IApiKey, ICreateApiKeyRequest, IBrandUsageSeries, IKeyUsageDetail, IUsageRange } from "./types";

const apiKeysUrl = `${environment.v2Url}/api-keys`;

const rangeQuery = (range?: IUsageRange): string => {
    const params = new URLSearchParams();
    if (range?.from) params.set('from', range.from);
    if (range?.to) params.set('to', range.to);
    if (range?.bucket) params.set('bucket', range.bucket);
    const s = params.toString();
    return s ? `&${s}` : '';
};

export const useApiKeys = () => {
    const [keys, setKeys] = useState<IApiKey[]>([]);
    const [loading, setLoading] = useState(false);
    const [, authToken, loginFunctions] = useLogin();
    const loginFunctionsRef = useRef(loginFunctions);

    useEffect(() => {
        loginFunctionsRef.current = loginFunctions;
    }, [loginFunctions]);

    // brandId 0 (or omitted) lists keys across all brands.
    const listKeys = useCallback(async (brandId: number = 0) => {
        setLoading(true);
        const url = brandId ? `${apiKeysUrl}?brand_id=${brandId}` : apiKeysUrl;
        const result = await Axios.get(url, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctionsRef.current.logout));

        if (result) {
            setKeys(result.data as IApiKey[]);
        }
        setLoading(false);
    }, [authToken]);

    const createKey = useCallback(async (input: ICreateApiKeyRequest): Promise<IApiKey | null> => {
        const result = await Axios.post(apiKeysUrl, input, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctionsRef.current.logout));

        if (result) {
            const created = result.data as IApiKey;
            setKeys(prev => [created, ...prev]);
            return created;
        }
        return null;
    }, [authToken]);

    const updateHosts = useCallback(async (id: number, permitted_hosts: string[]): Promise<IApiKey | null> => {
        const result = await Axios.patch(`${apiKeysUrl}/${id}`, { permitted_hosts }, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctionsRef.current.logout));

        if (result) {
            const updated = result.data as IApiKey;
            setKeys(prev => prev.map(k => (k.id === id ? updated : k)));
            return updated;
        }
        return null;
    }, [authToken]);

    const revokeKey = useCallback(async (id: number): Promise<boolean> => {
        const result = await Axios.delete(`${apiKeysUrl}/${id}`, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctionsRef.current.logout));

        if (result) {
            setKeys(prev => prev.map(k =>
                k.id === id ? { ...k, is_active: false, revoked_at: new Date().toISOString() } : k
            ));
            return true;
        }
        return false;
    }, [authToken]);

    // Per-key usage time-series for a brand (graphs).
    const listUsage = useCallback(async (brandId: number, range?: IUsageRange): Promise<IBrandUsageSeries[] | null> => {
        const url = `${apiKeysUrl}/usage?brand_id=${brandId || ''}${rangeQuery(range)}`;
        const result = await Axios.get(url, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctionsRef.current.logout));
        return result ? (result.data as IBrandUsageSeries[]) : null;
    }, [authToken]);

    // Single-key usage detail (series + per-endpoint breakdown).
    const getKeyUsage = useCallback(async (id: number, range?: IUsageRange): Promise<IKeyUsageDetail | null> => {
        const url = `${apiKeysUrl}/${id}/usage?${rangeQuery(range).replace(/^&/, '')}`;
        const result = await Axios.get(url, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctionsRef.current.logout));
        return result ? (result.data as IKeyUsageDetail) : null;
    }, [authToken]);

    return { keys, loading, listKeys, createKey, updateHosts, revokeKey, listUsage, getKeyUsage };
};
